import type { DataTableModel } from '../atoms'
import type { DataTableStateModel } from '../atoms/reatom-data-table-state'
import type { TableFilter } from '../atoms/table-filter'
import type { StrictTransFn } from '@root/shared/localization'
import type BigNumber from 'bignumber.js'
import type React from 'react'
import type { CSSProp } from 'styled-components'

export type ColumnJustify = 'start' | 'center' | 'end' | 'stretch'

export type DataTableCellProps<
  D extends object,
  ColumnId extends string = string,
  Context extends object | undefined = undefined,
> = {
  index: number
  value: D
  model: DataTableModel<D, ColumnId, object, Context>
  column: Column<D, ColumnId, Context>
  context: Context
}

type ColumnSizing = {
  width?: number | `${string}fr`
  minWidth?: number
  maxWidth?: number
}

export type ColumnHeaderRenderProps<
  D extends object = object,
  ColumnId extends string = string,
  Context extends object | undefined = undefined,
> = {
  column: Column<D, ColumnId, Context>
  model: DataTableModel<D, ColumnId, object, Context>
  context: Context
}

type ColumnRendering<
  D extends object = object,
  ColumnId extends string = string,
  Context extends object | undefined = undefined,
> =
  | ({
      hidden?: false
      header:
        | string
        | ((props: ColumnHeaderProps<D, ColumnId, Context>) => React.ReactNode)
      renderHeader?: (
        props: ColumnHeaderRenderProps<D, ColumnId, Context>,
      ) => React.ReactNode
      render: (
        args: DataTableCellProps<D, ColumnId, Context>,
      ) => React.ReactNode
      justify?: ColumnJustify
      css?: CSSProp
    } & ColumnSizing)
  | {
      hidden: true
    }

export type ColumnHeaderProps<
  D extends object,
  ColumnId extends string,
  Context extends object | undefined = undefined,
> = {
  columnId: ColumnId
  t: StrictTransFn
  // TODO: Figure out better way to type filter for filterable
  filter: TableFilter<ColumnId> | undefined
  setColumnFilter: (filter: TableFilter<ColumnId>) => void
  clearColumnFilter: () => void
  itemsStateAtom: DataTableStateModel<D>
  style: React.CSSProperties
  context: Context
}

export type Column<
  D extends object = object,
  ColumnId extends string = string,
  Context extends object | undefined = undefined,
> = {
  id: ColumnId

  /**
   * Used in filters and sorting
   */
  getValue?: (value: D) => string | number | BigNumber | Date
  sortable?: boolean
  filterable?: boolean
} & ColumnRendering<D, ColumnId, Context>

export const createColumnGridTemplate = <
  D extends object,
  ColumnId extends string = string,
>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ReadonlyArray<Column<D, ColumnId, any>>,
) => {
  return columns
    .filter((column) => !column.hidden)
    .map((column) => {
      if (typeof column.width === 'number') {
        return `${column.width}px`
      }
      return column.width || '1fr'
    })
    .join(' ')
}

export type DataTableNoAccessError = {
  type: 'DataTableNoAccessError'
}

export const dataTableNoAccessError = (): DataTableNoAccessError => {
  return {
    type: 'DataTableNoAccessError',
  }
}

export const isNoAccessError = (
  error: unknown,
): error is DataTableNoAccessError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'type' in error &&
    error.type === 'DataTableNoAccessError'
  )
}
